import React from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import {
  Home,
  BarChart3,
  Camera,
  Trophy,
  User,
  Activity,
  Brain,
  Target,
  LogOut,
  Apple
} from 'lucide-react';


import {
  Sidebar,
  SidebarContent,
  SidebarGroup,
  SidebarGroupContent,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  useSidebar,
  SidebarHeader,
  SidebarFooter
} from '@/components/ui/sidebar';

import { Badge } from '@/components/ui/badge';
import { useAuth } from '@/components/auth/AuthProvider';

const navigationItems = [
  {
    title: 'Dashboard',
    url: '/dashboard',
    icon: Home
  },
  {
    title: 'Progress Photos',
    url: '/photos',
    icon: Camera,
    badge: 'New'
  },
  {
    title: 'Activity Tracker',
    url: '/activity-tracker',
    icon: Activity
  },
  {
    title: 'Nutrition Tracker',
    url: '/nutrition',
    icon: Apple
  },
  {
    title: 'Analytics',
    url: '/analytics',
    icon: BarChart3
  },
  {
    title: 'AI Run Coach',
    url: '/ai-coach',
    icon: Brain
  },
  {
    title: 'AI Fitness Coach',
    url: '/ai-coach-fitness',
    icon: Target
  },
  {
    title: 'Achievements',
    url: '/achievements',
    icon: Trophy
  },
  {
    title: 'Profile',
    url: '/profile',
    icon: User
  }
];

export function AppSidebar() {
  const { state, isMobile, openMobile, setOpenMobile } = useSidebar();
  const location = useLocation();
  const { signOut } = useAuth();
  const currentPath = location.pathname;

  const handleSignOut = async () => {
    try {
      await signOut();
    } catch (error) {
      // Error is handled by the auth system
    } finally {
      if (isMobile) setOpenMobile(false);
    }
  };

  const handleNavClick = () => {
    if (isMobile) setOpenMobile(false);
  };

  const isActive = (path: string) => {
    if (path === '/dashboard') {
      return currentPath === '/' || currentPath === '/dashboard';
    }
    return currentPath === path;
  };

  const getNavClassName = (path: string) => {
    const baseClasses = "transition-colors duration-200";
    return isActive(path)
      ? `${baseClasses} bg-primary text-primary-foreground font-medium`
      : `${baseClasses} hover:bg-accent hover:text-accent-foreground`;
  };

  const collapsed = state === 'collapsed';

  // Auto-close mobile sidebar after navigation (route change only)
  React.useEffect(() => {
    if (isMobile) {
      setOpenMobile(false);
    }
  }, [currentPath, isMobile, setOpenMobile]);

  return (
    <Sidebar className="border-r border-border bg-background">
      {/* Header with Logo */}
      <SidebarHeader className="border-b border-border p-4">
        <div className="flex items-center gap-3">
          <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-gradient-to-br from-primary to-accent shadow-md">
            <Brain className="h-6 w-6 text-primary-foreground" />
          </div>
          {!collapsed && (
            <div className="flex flex-col">
              <span className="text-lg font-bold bg-gradient-to-r from-primary to-accent bg-clip-text text-transparent">
                FitAI Coach
              </span>
              <span className="text-xs text-muted-foreground">
                Your AI Fitness Companion
              </span>
            </div>
          )}
        </div>
      </SidebarHeader>

      <SidebarContent className="px-2 py-4">
        <SidebarGroup>
          <SidebarGroupContent>
            <SidebarMenu>
              {navigationItems.map((item) => (
                <SidebarMenuItem key={item.title}>
                  <SidebarMenuButton asChild className="h-10" onClick={handleNavClick}>
                    <NavLink
                      to={item.url}
                      end={item.url === '/'}
                      className={getNavClassName(item.url)}
                    >
                      <item.icon className="h-4 w-4 flex-shrink-0" />
                      {!collapsed && (
                        <span className="flex-1 text-left">{item.title}</span>
                      )}
                      {!collapsed && item.badge && (
                        <Badge 
                          variant="secondary" 
                          className="text-xs bg-primary/10 text-primary border-primary/20"
                        >
                          {item.badge}
                        </Badge>
                      )}
                    </NavLink>
                  </SidebarMenuButton>
                </SidebarMenuItem>
              ))}
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>
      </SidebarContent>

      {/* Footer */}
      <SidebarFooter className="border-t border-border p-2">
        <SidebarMenu>
          <SidebarMenuItem>
            <SidebarMenuButton 
              onClick={handleSignOut}
              className="h-10 w-full text-destructive hover:bg-destructive/10 hover:text-destructive"
            >
              <LogOut className="h-4 w-4 flex-shrink-0" />
              {!collapsed && (
                <span className="flex-1 text-left">Sign Out</span>
              )}
            </SidebarMenuButton>
          </SidebarMenuItem>
        </SidebarMenu>
      </SidebarFooter>
    </Sidebar>
  );
}